import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { Loader2 } from 'lucide-react';
import type { Sound } from '../types/database';

const soundSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  url: z.string().url('Please enter a valid URL')
    .refine(url => {
      return url.includes('spotify.com') || url.includes('soundcloud.com');
    }, 'Only Spotify and SoundCloud URLs are supported'),
});

type SoundFormData = z.infer<typeof soundSchema>;

interface SoundFormProps {
  bandId: string;
  sound?: Sound;
  onSuccess: () => void;
  onCancel: () => void;
}

export function SoundForm({ bandId, sound, onSuccess, onCancel }: SoundFormProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { register, handleSubmit, formState: { errors }, watch } = useForm<SoundFormData>({
    resolver: zodResolver(soundSchema),
    defaultValues: {
      title: sound?.title || '',
      url: sound?.url || '',
    },
  });

  const url = watch('url');
  const provider = url?.includes('spotify.com') ? 'spotify' : 'soundcloud';

  const onSubmit = async (data: SoundFormData) => {
    if (submitting) return; // Prevent multiple submissions
    
    try {
      setSubmitting(true);
      setError('');

      if (sound) {
        // Update existing sound
        const { error: updateError } = await supabase
          .from('sounds')
          .update({
            title: data.title,
            url: data.url,
            provider,
          })
          .eq('id', sound.id);

        if (updateError) throw updateError;
      } else {
        // Create new sound
        const { error: insertError } = await supabase
          .from('sounds')
          .insert({
            band_id: bandId,
            title: data.title,
            url: data.url,
            provider,
          });

        if (insertError) throw insertError;
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving sound:', err);
      setError(err instanceof Error ? err.message : 'Failed to save sound');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg text-center">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="title" className="block text-sm font-medium text-light/80 mb-2">
          {t('bands.sounds.soundTitle')}
        </label>
        <input
          type="text"
          id="title"
          {...register('title')}
          className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
          placeholder={t('bands.sounds.enterSoundTitle')}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-500">{errors.title.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="url" className="block text-sm font-medium text-light/80 mb-2">
          {t('bands.sounds.soundUrl')}
        </label>
        <input
          type="url"
          id="url"
          {...register('url')}
          className="w-full px-4 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:border-gray-custom/40 focus:outline-none"
          placeholder={t('bands.sounds.enterSoundUrl')}
        />
        {errors.url && (
          <p className="mt-1 text-sm text-red-500">
            {errors.url.message === 'Only Spotify and SoundCloud URLs are supported' 
              ? t('bands.sounds.onlySpotifyAndSoundcloud')
              : errors.url.message
            }
          </p>
        )}
      </div>

      <div className="flex flex-col md:flex-row justify-end md:space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn-outline order-2 mt-6 md:mt-0"
          disabled={submitting}
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="btn-secondary md:order-2"
          disabled={submitting}
        >
          {submitting ? (
            <span className="flex items-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>{sound ? t('bands.sounds.updatingSound') : t('bands.sounds.addingSound')}</span>
            </span>
          ) : (
            sound ? t('bands.sounds.editSound') : t('bands.sounds.addSound')
          )}
        </button>
      </div>
    </form>
  );
}